import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles, Info,
    AlertTriangle, Shield
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_AMENAZA = [
    { id: 'rechazo', label: 'Rechazo', description: 'Miedo a ser rechazado o no aceptado' },
    { id: 'fracaso', label: 'Fracaso', description: 'Miedo a no lograr lo esperado' },
    { id: 'incompetencia', label: 'Incompetencia', description: 'Miedo a ser visto como incapaz' },
    { id: 'exposicion', label: 'Exposición', description: 'Miedo a quedar expuesto o vulnerable' },
    { id: 'perdida_control', label: 'Pérdida de control', description: 'Miedo a perder el control de la situación' },
    { id: 'decepcion', label: 'Decepcionar', description: 'Miedo a decepcionar a otros o a ti mismo' },
    { id: 'abandono', label: 'Abandono', description: 'Miedo a ser dejado solo o sin apoyo' },
    { id: 'juicio', label: 'Juicio negativo', description: 'Miedo a ser juzgado o criticado' }
];

export default function Modulo21MapaAmenaza() {
    const [amenazaSeleccionada, setAmenazaSeleccionada] = useState('');
    const [contextoActivacion, setContextoActivacion] = useState('');
    const [respuestaConductual, setRespuestaConductual] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [tareaFaro, setTareaFaro] = useState('');

    useEffect(() => {
        const loadData = async () => {
            try {
                // Load draft
                const draftResponse = await axios.get(`${API_URL}/api/user/draft/mapa_amenaza`, {
                    withCredentials: true
                });
                if (draftResponse.data.data) {
                    const d = draftResponse.data.data;
                    if (d.amenazaSeleccionada) setAmenazaSeleccionada(d.amenazaSeleccionada);
                    if (d.contextoActivacion) setContextoActivacion(d.contextoActivacion);
                    if (d.respuestaConductual) setRespuestaConductual(d.respuestaConductual);
                }

                // Load lighthouse task from profile
                const profileResponse = await axios.get(`${API_URL}/api/user/profile`, {
                    withCredentials: true
                });
                if (profileResponse.data.lighthouse_task) {
                    setTareaFaro(profileResponse.data.lighthouse_task);
                } else if (profileResponse.data.baseline?.tarea_faro) {
                    setTareaFaro(profileResponse.data.baseline.tarea_faro);
                }
                if (draftResponse.data.read_only) { setReadOnly(true); setSubmittedAt(draftResponse.data.submitted_at); }
            } catch (error) {
                // silent - data load failure handled gracefully
            } finally {
                setLoadingDraft(false);
            }
        };
        loadData();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { module_id: 'mapa_amenaza', data: { amenazaSeleccionada, contextoActivacion, respuestaConductual } },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [amenazaSeleccionada, contextoActivacion, respuestaConductual]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [amenazaSeleccionada, contextoActivacion, respuestaConductual, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && amenazaSeleccionada && 
                      contextoActivacion.trim().length > 0 && 
                      respuestaConductual.trim().length > 0;

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) {
            toast.error('Completa todos los campos requeridos');
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/mapa-amenaza/submit`,
                { 
                    amenaza_tipo: amenazaSeleccionada,
                    contexto_activacion: contextoActivacion,
                    respuesta_conductual: respuestaConductual,
                    tarea_faro_referencia: tareaFaro
                },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al enviar';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl animate-fade-in-up">
                    <CardContent className="pt-8 text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                            <Shield className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Amenaza identificada</h2>
                        <p className="text-[#5FAF9D] font-medium mb-4">
                            {TIPOS_AMENAZA.find(t => t.id === amenazaSeleccionada)?.label}
                        </p>
                        
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                                    <p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p>
                                </div>
                            </div>
                        )}
                        
                        <div className="mb-6"><ModuleFeedback moduleId="mapa_amenaza" chapter={2} /></div>
                        <Link to="/usuario">
                            <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">
                                Continuar
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#3E5F6E]">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 2</span>
                                    <span className="text-xs text-[#7A8A93]">•</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 2.1</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Mapa de amenaza central</h1>
                            </div>
                        </div>
                        {savingDraft && !readOnly && (
                            <span className="text-xs text-[#7A8A93] flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Guardando...
                            </span>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E]/5 to-transparent p-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-xl flex items-center justify-center shadow-md">
                                <AlertTriangle className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#1F2A30]">Identifica tu amenaza central</CardTitle>
                                <p className="text-sm text-[#4A5B63]">¿Qué se activa cuando enfrentas tareas importantes?</p>
                            </div>
                        </div>
                        
                        {tareaFaro && (
                            <div className="p-4 bg-[#5FAF9D]/10 rounded-xl border border-[#5FAF9D]/20">
                                <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-1">Tu tarea faro</p>
                                <p className="text-[#1F2A30] text-sm">{tareaFaro}</p>
                            </div>
                        )}
                    </CardHeader>
                    
                    <CardContent className="p-8 space-y-8">
                        {/* Threat Selection */}
                        <div className="space-y-4">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">
                                ¿Qué amenaza interna se activa cuando piensas en esta tarea?
                            </Label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {TIPOS_AMENAZA.map((amenaza) => (
                                    <button
                                        key={amenaza.id}
                                        onClick={() => setAmenazaSeleccionada(amenaza.id)}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                                            amenazaSeleccionada === amenaza.id
                                                ? 'border-[#3E5F6E] bg-[#3E5F6E]/5 shadow-md'
                                                : 'border-[#DCE3E8] hover:border-[#3E5F6E]/50'
                                        }`}
                                        data-testid={`amenaza-${amenaza.id}`}
                                    >
                                        <p className={`font-medium text-sm ${
                                            amenazaSeleccionada === amenaza.id ? 'text-[#3E5F6E]' : 'text-[#1F2A30]'
                                        }`}>
                                            {amenaza.label}
                                        </p>
                                        <p className="text-xs text-[#7A8A93] mt-1">{amenaza.description}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Context */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">
                                ¿En qué contexto se activa esta amenaza?
                            </Label>
                            <Textarea
                                value={contextoActivacion}
                                onChange={(e) => setContextoActivacion(e.target.value)}
                                placeholder="Ej: Cuando tengo que presentar algo frente a otros, cuando hay una fecha límite cerca, cuando alguien evaluará mi trabajo..."
                                className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[100px]"
                                data-testid="contexto-input"
                            />
                        </div>

                        {/* Behavioral Response */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">
                                ¿Cuál es tu respuesta conductual típica?
                            </Label>
                            <Textarea
                                value={respuestaConductual}
                                onChange={(e) => setRespuestaConductual(e.target.value)}
                                placeholder="Ej: Me paralizo, busco distracciones, empiezo otras tareas menos importantes, me justifico..."
                                className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[100px]"
                                data-testid="respuesta-input"
                            />
                        </div>

                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button
                                onClick={handleSubmit}
                                disabled={!canSubmit || loading}
                                className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl shadow-md hover:shadow-lg transition-all"
                                data-testid="submit-mapa-amenaza"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        Completar módulo
                                        <Check className="h-4 w-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
